import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Button, Modal, message, Tag, Select, Input, Space, Switch } from "antd";
import { ReloadOutlined, DeleteOutlined, PlusOutlined, DollarOutlined, ShopOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import usePermissions from "../../../hooks/usePermissions";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import AddVendorExpenseModal from "../components/AddVendorExpenseModal";
import {
  fetchExpenseTypes,
  invalidateExpenseTypesCache,
  expenseTypeLabelMap,
} from "../lib/expenseTypes";
import useTablePagination from "../../../shared/ui/useTablePagination";

const FarmerExpenses = ({ embedded = false } = {}) => {
  const { t, i18n } = useTranslation();
  const { pagination } = useTablePagination();
  const { hasPermission, hasAny } = usePermissions();
  const canAddExpenses = hasAny(["vendors.expenses_add", "vendors.expenses"]);
  const canCancelExpenses = hasAny(["vendors.expenses_cancel", "vendors.expenses"]);
  const canExpenses = canAddExpenses || canCancelExpenses;
  const canViewExpenseTypes = hasAny([
    "vendors.expense_types_view",
    "vendors.expense_types_add",
    "vendors.expense_types_edit",
    "vendors.expense_types_delete",
  ]);
  const canAddExpenseTypes = hasPermission("vendors.expense_types_add");
  const canEditExpenseTypes = hasPermission("vendors.expense_types_edit");
  const canDeleteExpenseTypes = hasPermission("vendors.expense_types_delete");
  const [rows, setRows] = useState([]);
  const [knownProducts, setKnownProducts] = useState([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vendorFilter, setVendorFilter] = useState(null);
  const [productFilter, setProductFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [range, setRange] = useState([null, null]);
  const mainCode = readCachedSystemConfig()?.currency_code || "IQD";

  const [expenseTypes, setExpenseTypes] = useState([]);

  // Add expense modal
  const [addOpen, setAddOpen] = useState(false);

  // Manage types modal
  const [typesOpen, setTypesOpen] = useState(false);
  const [typesRows, setTypesRows] = useState([]);
  const [newTypeName, setNewTypeName] = useState("");
  const [typesSaving, setTypesSaving] = useState(false);

  const expLabels = useMemo(() => expenseTypeLabelMap(expenseTypes), [expenseTypes]);
  const expenseCategoryCodes = useMemo(
    () => (expenseTypes.length ? expenseTypes.map((x) => x.code) : ["rent", "transport", "moving", "watching", "other"]),
    [expenseTypes]
  );

  const loadTypes = useCallback(async (all = false) => {
    const rows = await fetchExpenseTypes({ all, force: true });
    setExpenseTypes(rows.filter((r) => r.is_active !== false));
    if (all) setTypesRows(rows);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (productFilter) params.product_id = productFilter;
      if (typeFilter) params.type = typeFilter;
      if (includeArchived) params.include_archived = 1;
      if (range[0]) params.start_date = dayjs(range[0]).format("YYYY-MM-DD");
      if (range[1]) params.end_date = dayjs(range[1]).format("YYYY-MM-DD");
      const res = await apiClient.get("/vendor-expenses", { params });
      const expenses = Array.isArray(res.data?.expenses) ? res.data.expenses : [];
      setRows(expenses);
      setKnownProducts((current) => {
        const products = new Map(current.map((product) => [product.id, product]));
        expenses.forEach((expense) => {
          if (expense.product_id) {
            products.set(expense.product_id, {
              id: expense.product_id,
              name: expense.product_name || `#${expense.product_id}`,
            });
          }
        });
        return [...products.values()];
      });
      setActiveTotal(parseFloat(res.data?.active_total || 0));
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [vendorFilter, productFilter, typeFilter, includeArchived, range, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTypes(false); }, [loadTypes]);
  useEffect(() => {
    apiClient.get("/vendors").then((r) => setVendors(Array.isArray(r.data) ? r.data : [])).catch(() => setVendors([]));
  }, []);

  const openAdd = () => setAddOpen(true);

  const openTypes = async () => {
    setNewTypeName("");
    await loadTypes(true);
    setTypesOpen(true);
  };

  const addType = async () => {
    const name = newTypeName.trim();
    if (!name) { message.error(t("expenseTypeNameRequired", "Enter a type name")); return; }
    setTypesSaving(true);
    try {
      await apiClient.post("/farmer-expense-types", { name });
      message.success(t("expenseTypeAdded", "Expense type added"));
      setNewTypeName("");
      invalidateExpenseTypesCache();
      await loadTypes(true);
      await loadTypes(false);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed"));
    } finally {
      setTypesSaving(false);
    }
  };

  const renameType = async (row, name) => {
    const next = (name || "").trim();
    if (!next || next === row.name) return;
    try {
      await apiClient.put(`/farmer-expense-types/${row.id}`, { name: next });
      invalidateExpenseTypesCache();
      await loadTypes(true);
      await loadTypes(false);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed"));
    }
  };

  const deactivateType = (row) => {
    Modal.confirm({
      title: t("expenseTypeRemoveConfirm", "Hide this expense type?"),
      content: t("expenseTypeRemoveHint", "It stays for old records but won’t show when adding new expenses."),
      okText: t("yesDelete", "Yes"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiClient.delete(`/farmer-expense-types/${row.id}`);
          invalidateExpenseTypesCache();
          await loadTypes(true);
          await loadTypes(false);
          message.success(t("expenseTypeRemoved", "Expense type hidden"));
        } catch (e) {
          message.error(e?.response?.data?.message || t("loadError", "Failed"));
        }
      },
    });
  };

  const cancel = (record) => {
    Modal.confirm({
      title: t("vendorCancelExpenseConfirm", "Cancel this expense?"),
      content: t("vendorCancelExpenseHint", "It stays on record as cancelled and is no longer counted."),
      okText: t("vendorCancelExpense", "Cancel expense"),
      okButtonProps: { danger: true },
      cancelText: t("back", "Back"),
      onOk: async () => {
        try {
          await apiClient.post(`/vendor-expenses/${record.id}/cancel`);
          message.success(t("vendorExpenseCancelled", "Expense cancelled"));
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("vendorExpenseFailed", "Could not cancel expense"));
        }
      },
    });
  };

  const listedRows = useMemo(() => {
    if (!typeFilter) return rows;
    return rows.filter((r) => String(r.type || "") === String(typeFilter));
  }, [rows, typeFilter]);

  const listedTotal = useMemo(
    () => listedRows.reduce((s, r) => (r.cancelled_at ? s : s + (parseFloat(r.amount) || 0)), 0),
    [listedRows]
  );
  const listedCount = useMemo(
    () => listedRows.filter((r) => !r.cancelled_at).length,
    [listedRows]
  );

  const typeOptions = useMemo(() => {
    const map = new Map();
    expenseCategoryCodes.forEach((code) => {
      map.set(code, expLabels[code] || code);
    });
    rows.forEach((r) => {
      const code = String(r.type || "").trim();
      if (code && !map.has(code)) map.set(code, expLabels[code] || code);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [expenseCategoryCodes, expLabels, rows]);

  const productOptions = useMemo(
    () => knownProducts.map((product) => ({
      value: product.id,
      label: parseLocalizedName(product.name, i18n.language, `#${product.id}`),
    })),
    [knownProducts, i18n.language]
  );

  const columns = [
    { title: "#", key: "idx", width: 46, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("vendorsColName", "Farmer")}</b>, key: "vendor",
      render: (_, r) => (r.vendor_id
        ? <Link to={`/vendors/${r.vendor_id}`} style={{ fontWeight: 600 }}>{r.vendor_name || `#${r.vendor_id}`}</Link>
        : <span style={{ color: T.muted }}>—</span>),
    },
    {
      title: <b>{t("type", "Type")}</b>, dataIndex: "type", key: "type",
      render: (v, r) => (
        <Tag color={r.cancelled_at ? "default" : (r.settlement_archive_id ? "purple" : "orange")}>
          {expLabels[v] || r.type_name || v || "—"}
        </Tag>
      ),
    },
    {
      title: <b>{t("product", "Product")}</b>, key: "product",
      render: (_, r) => r.product_id
        ? (
          <span style={{ fontWeight: 600 }}>
            {parseLocalizedName(r.product_name, i18n.language, `#${r.product_id}`)}
          </span>
        )
        : <span style={{ color: T.muted }}>{t("vendorExpenseFullAccount", "Full account")}</span>,
    },
    {
      title: <b>{t("amountPaid", "Amount")}</b>, dataIndex: "amount", key: "amount", align: "right",
      render: (v, r) => r.cancelled_at
        ? <span style={{ color: T.muted, textDecoration: "line-through" }}>{formatCurrency(v, mainCode)}</span>
        : <b style={{ color: T.red }}>{formatCurrency(v, mainCode)}</b>,
    },
    {
      title: <b>{t("note", "Note")}</b>, dataIndex: "note", key: "note",
      render: (v) => <span style={{ color: T.sub }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("date", "Date")}</b>, dataIndex: "created_at", key: "created_at", width: 140,
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"}</span>,
    },
    {
      title: <b>{t("createdBy", "Added by")}</b>, dataIndex: "created_by_name", key: "created_by_name",
      render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("status", "Status")}</b>, key: "status", width: 190, align: "center",
      render: (_, r) => {
        if (r.cancelled_at) {
          return (
            <span style={{ fontSize: 11, color: T.red }}>
              ✕ {t("vendorExpenseCancelledBy", "Cancelled")}
              {r.cancelled_by_name ? ` · ${r.cancelled_by_name}` : ""}
            </span>
          );
        }
        if (r.settlement_archive_id) {
          return <Tag color="purple">{t("archived", "Archived")}</Tag>;
        }
        return canCancelExpenses
          ? <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => cancel(r)}>{t("cancel", "Cancel")}</Button>
          : <Tag color="green">{t("active", "Active")}</Tag>;
      },
    },
  ];

  const expenseActions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>{t("currencyTotalsRefresh", "Refresh")}</Button>
      {canViewExpenseTypes && (
        <Button icon={<SettingOutlined />} onClick={openTypes} style={{ borderRadius: 9 }}>
          {t("expenseTypesManage", "Expense types")}
        </Button>
      )}
      {canAddExpenses && (
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ borderRadius: 9 }}>{t("vendorAddExpense", "Add expense")}</Button>
      )}
    </div>
  );

  return (
    <div style={{ padding: embedded ? 0 : 24, background: T.bg, minHeight: embedded ? "auto" : "100%" }}>
      {!embedded && (
        <PageHeader
          breadcrumb={[t("vendorsMenu", "Farmers"), t("farmerExpensesTitle", "Farmer Expenses")]}
          title={t("farmerExpensesTitle", "Farmer Expenses")}
          subtitle={canExpenses
            ? t("farmerExpensesHint", "Add costs to a farmer's account; cancel a wrong one (it stays listed as cancelled)")
            : t("farmerExpensesViewHint", "View costs charged to farmers' accounts")}
          action={expenseActions}
        />
      )}
      {embedded && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          {expenseActions}
        </div>
      )}

      <div className="velan-stats-row">
        <StatCard
          icon={<DollarOutlined style={{ color: T.red }} />}
          label={includeArchived
            ? t("farmerExpensesListedTotal", "Listed total")
            : t("batchExpenses", "Expenses")}
          value={formatCurrency(listedTotal, mainCode)}
          iconBg="#fee2e2"
          valueColor={T.red}
        />
        {includeArchived && (
          <StatCard
            icon={<DollarOutlined style={{ color: T.green }} />}
            label={t("farmerExpensesOpenTotal", "Open (not archived)")}
            value={formatCurrency(activeTotal, mainCode)}
            iconBg="#dcfce7"
            valueColor={T.green}
          />
        )}
        <StatCard
          icon={<ShopOutlined style={{ color: T.primary }} />}
          label={t("count", "Count")}
          value={listedCount}
          iconBg="#dbeafe"
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, padding: "14px 16px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder={t("vendorsColName", "Farmer")}
          style={{ minWidth: 220 }}
          value={vendorFilter}
          onChange={(v) => setVendorFilter(v ?? null)}
          options={vendors.map((v) => ({ value: v.id, label: v.name }))}
        />
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder={t("expenseTypeFilter", "Expense type")}
          style={{ minWidth: 180 }}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v ?? null)}
          options={typeOptions}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={t("product", "Product")}
          style={{ minWidth: 180 }}
          value={productFilter}
          onChange={(value) => setProductFilter(value ?? null)}
          options={productOptions}
        />
        <DateRangeFilter
          value={range}
          onChange={(dates) => setRange(dates || [null, null])}
          showTime={false}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginInlineStart: 4 }}>
          <Switch
            size="small"
            checked={includeArchived}
            onChange={setIncludeArchived}
          />
          <span style={{ fontSize: 13, color: T.sub, fontWeight: 600 }}>
            {t("showArchived", "Show Archived")}
          </span>
        </div>
      </div>

      {typeFilter && listedRows.length === 0 && !includeArchived && rows.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 12, padding: "10px 14px", fontSize: 13, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a" }}>
          {t(
            "farmerExpensesTypeArchivedHint",
            "No open expenses for this type. Turn on “Show Archived” to include settled ones."
          )}
        </div>
      )}

      <div style={cardStyle}>
        <Table
          size="small"
          loading={loading}
          dataSource={listedRows.map((r) => ({ ...r, key: r.id }))}
          columns={columns}
          pagination={pagination}
          scroll={{ x: 760 }}
        />
      </div>

      <AddVendorExpenseModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        vendors={vendors}
        onSuccess={async () => {
          try {
            const response = await apiClient.get("/vendors");
            setVendors(Array.isArray(response.data) ? response.data : []);
          } catch {
            /* keep current list */
          }
          load();
        }}
      />

      <Modal
        title={t("expenseTypesManage", "Expense types")}
        open={typesOpen}
        onCancel={() => setTypesOpen(false)}
        footer={null}
        centered
        destroyOnHidden
        width={520}
      >
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 12 }}>
          {t("expenseTypesHint", "These names appear when adding expenses and on farmer settlement prints.")}
        </div>
        {canAddExpenseTypes && (
          <Space.Compact style={{ width: "100%", marginBottom: 14 }}>
            <Input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder={t("expenseTypeName", "New type name (e.g. حمالیە)")}
              onPressEnter={addType}
            />
            <Button type="primary" loading={typesSaving} onClick={addType} icon={<PlusOutlined />}>
              {t("add", "Add")}
            </Button>
          </Space.Compact>
        )}
        <Table
          size="small"
          pagination={false}
          dataSource={typesRows.filter((r) => r.is_active !== false).map((r) => ({ ...r, key: r.id }))}
          locale={{ emptyText: t("noDataFound", "No data found") }}
          columns={[
            {
              title: <b>{t("name", "Name")}</b>,
              dataIndex: "name",
              render: (v, r) => (
                canEditExpenseTypes ? (
                  <Input
                    defaultValue={v}
                    onBlur={(e) => renameType(r, e.target.value)}
                    onPressEnter={(e) => { e.target.blur(); }}
                  />
                ) : (
                  <span>{v}</span>
                )
              ),
            },
            {
              title: <b>{t("code", "Code")}</b>,
              dataIndex: "code",
              width: 120,
              render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v}</span>,
            },
            ...(canDeleteExpenseTypes ? [{
              title: "",
              key: "rm",
              width: 80,
              align: "center",
              render: (_, r) => (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deactivateType(r)}>
                  {t("hide", "Hide")}
                </Button>
              ),
            }] : []),
          ]}
        />
      </Modal>
    </div>
  );
};

export default FarmerExpenses;
